/**
 * Custom error class for API responses that are not OK (e.g., 4xx, 5xx).
 *
 * Thrown by the post-processed `v3ApiFetcher.ts` / `v2ApiFetcher.ts` so consumers
 * can branch on `instanceof ApiError`, read the HTTP `status`, the parsed `body`,
 * and the response `headers`. Replaces the generated fetcher's default
 * "Network error (...)" wrapping which masks all of these fields at runtime.
 *
 * The post-process script (`scripts/postProcessCustomFetchers.ts`) rewrites
 * the generated fetcher to throw `ApiError.fromResponse(response)` and
 * `NetworkError(...)` instead of the default opaque wrapping.
 */
export class ApiError extends Error {
  public readonly status: number
  public readonly statusText: string
  // biome-ignore lint/suspicious/noExplicitAny: error bodies vary across endpoints
  public readonly body: any
  public readonly headers: Headers

  // biome-ignore lint/suspicious/noExplicitAny: error bodies vary across endpoints
  constructor(response: Response, body: any, message?: string) {
    // If no message is provided, use status text or a generic message
    super(message || response.statusText || `HTTP Error ${response.status}`)
    this.name = 'ApiError'

    this.status = response.status
    this.statusText = response.statusText
    this.body = body
    this.headers = response.headers

    // Maintain a proper stack trace in V8 (Chrome / Node), with a fallback for other engines
    if (typeof Error.captureStackTrace === 'function') {
      Error.captureStackTrace(this, ApiError)
    } else {
      this.stack = new Error(message).stack
    }
  }

  /**
   * Creates an `ApiError` from a `Response` object. Reads the body as text
   * first, then parses it as JSON if the content-type advertises a
   * JSON-flavoured media type (`application/json`,
   * `application/problem+json`, `application/vnd.api+json`, etc. — matched
   * by `/^[^;]*\/(?:[\w.+-]+\+)?json(?:\s|;|$)/i`, anchored to the type/subtype
   * portion to avoid false positives from a parameter ending in `+json`).
   * If JSON.parse fails on a malformed body, the raw text is returned so the
   * caller still has diagnostic information instead of a `null` body.
   */
  static async fromResponse(response: Response): Promise<ApiError> {
    // biome-ignore lint/suspicious/noExplicitAny: error bodies vary across endpoints
    let body: any = null
    try {
      const text = await response.text()
      const contentType = response.headers.get('content-type')
      const isJson = !!(
        contentType &&
        /^[^;]*\/(?:[\w.+-]+\+)?json(?:\s|;|$)/i.test(contentType)
      )
      if (isJson && text.length > 0) {
        try {
          body = JSON.parse(text)
        } catch {
          // Malformed JSON — keep the raw text so the caller has something
          // to log. Better than swallowing the body to `null`.
          body = text
        }
      } else {
        body = text
      }
    } catch (e) {
      console.warn('Failed to read error response body:', e)
    }
    return new ApiError(response, body)
  }
}

/**
 * Extracts a user-friendly error description from any thrown error.
 *
 * Falls back to `error.message` for any `Error`, and a generic message for
 * non-`Error` values. Extend this function to map known backend message
 * patterns to human-readable copy.
 */
export function getErrorDescription(error: unknown): string {
  return error instanceof Error ? error.message : 'Please try again later'
}

/**
 * Represents a network error (e.g., no internet connection, DNS failure)
 * that prevents a response from being received. Distinct from `ApiError`
 * which represents a response with a non-OK status.
 *
 * The original error (if any) is exposed via the standard ES2022 `cause`
 * property, accessible as `networkError.cause`.
 */
export class NetworkError extends Error {
  constructor(message?: string, originalError?: Error) {
    super(
      message || 'Network error occurred',
      originalError ? { cause: originalError } : undefined,
    )
    this.name = 'NetworkError'
    if (typeof Error.captureStackTrace === 'function') {
      Error.captureStackTrace(this, NetworkError)
    }
  }
}
